/**
 * @module modules/commit-range-sk
 * @description <h2><code>commit-range-sk</code></h2>
 *
 * Displays a link that describes a range of commits in a repo. This element
 * uses the global `window.perf.commit_range_url`, which can be set on Perf via
 * the command line.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "CommitRangeSk", {
    enumerable: true,
    get: function() {
        return CommitRangeSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _cid = require("../cid/cid");
const _const = require("../const/const");
const _json = require("../json");
require("../window/window");
const _commit = require("../common/commit");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
/** The default implementation for commitNumberToHashes run the commit numbers
 *  through cid lookup to get the hashes by making a request to the server.
 */ const defaultcommitNumberToHashes = async (cids)=>{
    const json = await (0, _cid.lookupCids)(cids);
    return [
        json.commitSlice[0].hash,
        json.commitSlice[1].hash
    ];
};
let CommitRangeSk = class CommitRangeSk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('trace') || changedProperties.has('commitIndex') || changedProperties.has('header') || changedProperties.has('hashes')) {
            this.recalcLink(changedProperties);
        }
    }
    reset() {
        this.commitIndex = -1;
        this.trace = [];
        this.header = null;
        this.hashes = null;
        this._text = '';
        this._url = '';
        this._commitIds = null;
    }
    clear() {
        this._text = '';
        this._url = '';
    }
    /** Check start and end commits and determines if delta is more than 1.
   * If so, it is a range and returns true.
   * If not, it is a single commit and returns false.
   * Returns false if no commits are set.
   * @returns boolean
   */ isRange() {
        if (!this._commitIds) {
            return null;
        }
        if (this._commitIds[0] >= this._commitIds[1]) {
            return false;
        }
        if (this._commitIds[0] + 1 === this._commitIds[1]) {
            return false;
        }
        return true;
    }
    /**
   * Sets the range explicitly.
   * @param start The previous commit (exclusive).
   * @param end The current commit (inclusive).
   */ async setRange(start, end) {
        this._commitIds = [
            start,
            end
        ];
        this.hashes = null;
        this.updateText();
        await this.recalcLink();
    }
    /**
   * Recalculates the link based on the current state of the object.
   * If there is not enough information to build the link, it clears the
   * current link.
   */ async recalcLink(changedProperties) {
        this.currentRequestId++;
        const requestId = this.currentRequestId;
        if (window.perf.commit_range_url === '') {
            this.clear();
            return;
        }
        if (this.commitIndex !== -1) {
            const newCommitIds = this.setCommitIds(this.commitIndex);
            if (!newCommitIds || newCommitIds.length !== 2) {
                this.clear();
                return;
            }
            // If the commit IDs have changed then the hashes are no longer valid.
            if (!this._commitIds || this._commitIds[0] !== newCommitIds[0] || this._commitIds[1] !== newCommitIds[1]) {
                var _changedProperties;
                if (!((_changedProperties = changedProperties) === null || _changedProperties === void 0 ? void 0 : _changedProperties.has('hashes'))) {
                    this.hashes = null;
                }
            }
            this._commitIds = newCommitIds;
        } else if (!this._commitIds) {
            // If commitIndex is -1 and no manual range set, clear.
            this.clear();
            return;
        }
        try {
            if (!this._commitIds || this._commitIds.length !== 2) {
                this.clear();
                return;
            }
            this.updateText();
            // Clear URL while fetching new hashes or if irrelevant
            this._url = '';
            if (!this.hashes) {
                // Run the commit numbers through cid lookup to get the hashes.
                const hashes = await this.commitNumberToHashes(this._commitIds);
                if (requestId !== this.currentRequestId) {
                    return;
                }
                this.hashes = hashes;
            }
            // If we have the hashes, then we can build the link.
            if (this.hashes && this.hashes.length > 1) {
                const url = this._buildUrl();
                if (requestId !== this.currentRequestId) {
                    return;
                }
                this._url = url;
                // Ensure element is connected to tooltip before dispatching event.
                if (this.isConnected) {
                    this.dispatchEvent(new CustomEvent('commit-range-changed', {
                        bubbles: true,
                        composed: true
                    }));
                }
            }
        } catch (error) {
            console.log(error);
            this.clear();
        }
    }
    // Should be as close as possible to perf/go/formatter.
    _buildUrl() {
        var _window_perf;
        let url = ((_window_perf = window.perf) === null || _window_perf === void 0 ? void 0 : _window_perf.commit_range_url) || '';
        // Always replace {end} with the second hash.
        if (url.includes('{end}')) {
            url = url.replace('{end}', this.hashes[1]);
        }
        const isRange = this.isRange();
        if (isRange) {
            // Handle range URLs (Googlesource)
            if (url.includes('{begin}')) {
                url = url.replace('{begin}', this.hashes[0]);
            } else {
                var _window_perf_show_hash_ranges_in_tooltip;
                // We expect a single commit range, but there are gaps in data.
                // We display hashes instead of commit positions not to confuse users.
                if ((_window_perf_show_hash_ranges_in_tooltip = window.perf.show_hash_ranges_in_tooltip) !== null && _window_perf_show_hash_ranges_in_tooltip !== void 0 ? _window_perf_show_hash_ranges_in_tooltip : false) {
                    this._handleHashRange();
                }
            }
        } else {
            // Handle single commit scenarios
            if (url.includes('+log/{begin}..')) {
                // Googlesource style: transform to single commit view
                url = url.replace('+log/{begin}..', '+/');
            } else {
                // Fallback for any other template, remove {begin} if it exists
                if (url.includes('{begin}')) {
                    url = url.replace('{begin}', '');
                }
                // If GitHub, show short hash instead of commit number.
                if (url.includes('github')) {
                    this._text = (0, _commit.TrimHash)(this.hashes[1]);
                }
            }
        }
        return url;
    }
    async _handleHashRange() {
        const oldText = this._text;
        this._text = 'loading...';
        const hash1 = (0, _commit.TrimHash)(this.hashes[1]);
        const numberOfCommitsInRangeText = `(${this._commitIds[1] - this._commitIds[0]} commits in this range)`;
        // defaultcommitNumberToHashes expects exactly two hashes.
        // We only care about the first one. For the other one, we grab whatever.
        // This solution makes this branch compatible with the caching mechanic used
        // in this.commitNumberToHashes.
        const hashAfter0UntrimmedList = await this.commitNumberToHashes([
            (0, _json.CommitNumber)(this._commitIds[0] + 1),
            (0, _json.CommitNumber)(this._commitIds[1])
        ]);
        if (hashAfter0UntrimmedList.length < 1) {
            console.log('failed to handle fetch commit info for beginning of the range.');
            this._text = oldText;
            return;
        }
        const hashAfter0 = (0, _commit.TrimHash)(hashAfter0UntrimmedList[0]);
        this._text = `${hashAfter0} - ${hash1} ${numberOfCommitsInRangeText}`;
    }
    updateText() {
        if (!this._commitIds || this._commitIds.length !== 2) {
            return;
        }
        let text = `${this._commitIds[1]}`;
        // Check if there are no points between start and end.
        const isRange = this.isRange();
        if (isRange) {
            // Add +1 to the previous commit to only show commits after previous.
            text = `${this._commitIds[0] + 1} - ${this._commitIds[1]}`;
        }
        this._text = text;
    }
    set autoload(val) {
        this._autoload = val;
    }
    setCommitIds(commitIndex) {
        var _this_header_prevCommit, _this_header_commitIndex;
        if (this.trace.length === 0 || this.header === null) {
            this.clear();
            return null;
        }
        // First the previous commit that has data.
        let prevCommit = commitIndex - 1;
        while(prevCommit > 0 && this.trace[prevCommit] === _const.MISSING_DATA_SENTINEL){
            prevCommit -= 1;
        }
        // If we don't find a second commit then we can't present the information.
        if (prevCommit < 0) {
            this.clear();
            return null;
        }
        var _this_header_prevCommit_offset;
        const startOffset = (_this_header_prevCommit_offset = (_this_header_prevCommit = this.header[prevCommit]) === null || _this_header_prevCommit === void 0 ? void 0 : _this_header_prevCommit.offset) !== null && _this_header_prevCommit_offset !== void 0 ? _this_header_prevCommit_offset : null;
        var _this_header_commitIndex_offset;
        const endOffset = (_this_header_commitIndex_offset = (_this_header_commitIndex = this.header[commitIndex]) === null || _this_header_commitIndex === void 0 ? void 0 : _this_header_commitIndex.offset) !== null && _this_header_commitIndex_offset !== void 0 ? _this_header_commitIndex_offset : null;
        if (startOffset === null || endOffset === null) {
            this.clear();
            return null;
        }
        return [
            startOffset,
            endOffset
        ];
    }
    render() {
        if (this._url) {
            return (0, _lit.html)`<a href="${this._url}" target="_blank">${this._text}</a>`;
        }
        return (0, _lit.html)`<span style="cursor: default">${this._text}</span>`;
    }
    constructor(...args){
        super(...args);
        this.trace = [];
        this.commitIndex = -1;
        this.header = null;
        this.hashes = null;
        this._text = '';
        this._url = '';
        this._autoload = true;
        this._commitIds = null;
        this.currentRequestId = 0;
        this.hashCache = new Map();
        // commitNumberToHashes can be replaced to make testing easier.
        this.commitNumberToHashes = async (cids)=>{
            const cacheKey = cids.join(',');
            if (this.hashCache.has(cacheKey)) {
                return this.hashCache.get(cacheKey);
            }
            let hashes = [];
            if (this._autoload) {
                hashes = await defaultcommitNumberToHashes(cids);
                this.hashCache.set(cacheKey, hashes);
                return hashes;
            }
            return [];
        };
    }
};
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Array)
], CommitRangeSk.prototype, "trace", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Number,
        attribute: 'commit-index'
    }),
    _ts_metadata("design:type", Number)
], CommitRangeSk.prototype, "commitIndex", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Object)
], CommitRangeSk.prototype, "header", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Object)
], CommitRangeSk.prototype, "hashes", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], CommitRangeSk.prototype, "_text", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], CommitRangeSk.prototype, "_url", void 0);
CommitRangeSk = _ts_decorate([
    (0, _decorators.customElement)('commit-range-sk')
], CommitRangeSk);
